import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, ChevronRight, ChevronDown, FileText, ExternalLink, Link2, Sparkles, RefreshCcw } from "lucide-react";

/**
 * Compact 2-level shared-workspace Drive preview for the sidebar.
 *   - Calls `/workspace/{id}/drive/info` to learn whether the shared
 *     `KhojByte/<project>` folder already exists.
 *   - Owner sees a "Set up shared workspace drive" CTA the first time.
 *   - Once initialised, everyone in the workspace lists the SAME folder
 *     (shared via Google Drive permissions) — files appear identically for
 *     all collaborators, fulfilling the "single shared drive" requirement.
 *   - Folder tree expands one level on click.
 */
export default function DrivePreviewSidebar({ hasDrive, onConnect, workspaceId }) {
  const [info, setInfo] = useState(null);
  const [root, setRoot] = useState(null); // {folders:[], files:[]}
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!hasDrive || !workspaceId) return;
    (async () => {
      try {
        const r = await api.get(`/workspace/${workspaceId}/drive/info`);
        setInfo(r.data);
        if (r.data?.folder_id) await loadRoot(r.data.folder_id);
      } catch { /* silent */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDrive, workspaceId]);

  const loadRoot = async (folderId) => {
    setLoading(true);
    try {
      const r = await api.get("/google/drive/list", { params: { folder_id: folderId } });
      const files = r.data?.files || [];
      const folders = files.filter((f) => f.mimeType === "application/vnd.google-apps.folder");
      const rest = files.filter((f) => f.mimeType !== "application/vnd.google-apps.folder");
      setRoot({ folders: folders.slice(0, 8), files: rest.slice(0, 12) });
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  };

  const toggleFolder = async (f) => {
    if (expanded[f.id]) {
      setExpanded((e) => { const n = { ...e }; delete n[f.id]; return n; });
      return;
    }
    try {
      const r = await api.get("/google/drive/list", { params: { folder_id: f.id } });
      setExpanded((e) => ({ ...e, [f.id]: (r.data?.files || []).slice(0, 8) }));
    } catch { /* silent */ }
  };

  const setup = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspace/${workspaceId}/drive/setup`);
      toast.success("Shared workspace drive ready — collaborators have been granted access");
      setInfo({ ...(info || {}), folder_id: r.data.folder_id, needs_setup: false, is_owner: true });
      await loadRoot(r.data.folder_id);
    } catch (e) {
      const status = e?.response?.status;
      // 428 → the drive-owner's stored Google token is missing the
      // drive.file scope. If the viewer IS the owner, kick them straight
      // to Google re-consent; otherwise surface the detail so they can
      // ping the assigner.
      if (status === 428 && info?.is_owner) {
        toast.message("Google Drive permission needed — redirecting to re-authorize…");
        onConnect?.();
        return;
      }
      toast.error(e?.response?.data?.detail || "Couldn't initialise shared drive");
    } finally {
      setBusy(false);
    }
  };

  const syncShares = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspace/${workspaceId}/drive/sync-shares`);
      toast.success(`Shared with ${r.data.shared_with?.length || 0} collaborator(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't refresh shares");
    } finally {
      setBusy(false);
    }
  };

  if (!hasDrive) {
    return (
      <div className="text-center py-2">
        <p className="text-xs text-kb-ink-muted mb-2">Connect Google Drive to access the shared workspace folder.</p>
        <button onClick={onConnect} className="kb-btn-outline text-xs" data-testid="sidebar-drive-connect">
          <Link2 className="w-3 h-3" /> Connect Drive
        </button>
      </div>
    );
  }

  if (info && info.needs_setup) {
    return info.is_owner ? (
      <div className="space-y-2" data-testid="drive-setup-owner">
        <p className="text-xs text-kb-ink-muted">
          A single <em>KhojByte / &lt;project&gt;</em> folder in <strong>your</strong> Drive is shared with every collaborator (read + write). Set it up once.
        </p>
        <button onClick={setup} disabled={busy} className="kb-btn-primary text-xs w-full justify-center" data-testid="drive-setup-btn">
          <Sparkles className="w-3 h-3" /> {busy ? "Setting up…" : "Set up shared workspace drive"}
        </button>
      </div>
    ) : (
      <div className="text-xs text-kb-ink-muted py-2" data-testid="drive-setup-pending">
        Shared workspace drive isn&apos;t initialised yet. The main client (workspace owner) needs to set it up from their Drive sidebar — once done it&apos;ll appear here automatically.
      </div>
    );
  }

  if (loading || !root) {
    return <div className="kb-mono text-[10px] text-kb-ink-muted py-2">Loading shared drive…</div>;
  }

  return (
    <div className="space-y-1" data-testid="drive-preview-sidebar">
      {info?.is_owner && (
        <button onClick={syncShares} disabled={busy} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-1 mb-1" data-testid="drive-resync-btn">
          <RefreshCcw className="w-3 h-3" /> Re-share with current collaborators
        </button>
      )}
      {root.folders.length === 0 && root.files.length === 0 ? (
        <div className="kb-mono text-[10px] text-kb-ink-muted py-2">Folder is empty — upload via the Drive tab.</div>
      ) : null}
      {root.folders.map((f) => {
        const isOpen = !!expanded[f.id];
        const kids = expanded[f.id] || [];
        return (
          <div key={f.id}>
            <button
              type="button"
              onClick={() => toggleFolder(f)}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left hover:bg-slate-50 rounded-sm transition-colors"
              data-testid={`drive-folder-${f.id}`}
            >
              {isOpen ? <ChevronDown className="w-3 h-3 text-kb-ink-muted shrink-0" /> : <ChevronRight className="w-3 h-3 text-kb-ink-muted shrink-0" />}
              <FolderOpen className="w-3.5 h-3.5 text-kb-primary shrink-0" />
              <span className="text-xs text-kb-ink truncate flex-1">{f.name}</span>
              {f.webViewLink && (
                <a href={f.webViewLink} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-kb-ink-light hover:text-kb-primary">
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </button>
            {isOpen && (
              <div className="ml-5 border-l border-slate-200 pl-2 space-y-0.5">
                {kids.length === 0 ? (
                  <div className="kb-mono text-[9px] text-kb-ink-light py-1">Empty folder</div>
                ) : kids.map((c) => (
                  <a key={c.id} href={c.webViewLink} target="_blank" rel="noreferrer"
                     className="flex items-center gap-1.5 px-1.5 py-1 text-left hover:bg-slate-50 rounded-sm transition-colors">
                    {c.mimeType === "application/vnd.google-apps.folder"
                      ? <FolderOpen className="w-3 h-3 text-kb-primary shrink-0" />
                      : <FileText className="w-3 h-3 text-kb-ink-muted shrink-0" />}
                    <span className="text-[11px] text-kb-ink truncate flex-1">{c.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {root.files.length > 0 && (
        <div className="pt-1 mt-1 border-t border-slate-100 space-y-0.5">
          {root.files.map((f) => (
            <a key={f.id} href={f.webViewLink} target="_blank" rel="noreferrer"
               className="flex items-center gap-1.5 px-2 py-1 hover:bg-slate-50 rounded-sm transition-colors">
              <FileText className="w-3 h-3 text-kb-ink-muted shrink-0" />
              <span className="text-xs text-kb-ink truncate flex-1">{f.name}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
